import { useNuxtApp } from 'nuxt/app';
import { isNativeApp } from '~/functions/appOrigin';

export default defineNuxtPlugin(() => {
  const { $config } = useNuxtApp();
  
  // Enable debug in local development when parameter is present, or when explicitly configured.
  // The hostname check must exclude native builds: Capacitor serves the app from
  // capacitor://localhost (iOS) / https://localhost (Android), so a bare hostname test
  // would report "local dev" in every production app build. See functions/appOrigin.ts.
  const isLocalHostname =
    !isNativeApp() &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const isLocalDev = process.dev || isLocalHostname;
  const isDebugConfigEnabled = $config.public.debugH5P;
  
  // Only initialize when debug is configured OR in local development
  if (!isDebugConfigEnabled && !isLocalDev) return;

  const isDebugEnabled = () => {
    if (!process.client) return false;

    // Case-insensitive check for debugH5P parameter
    const urlParams = new URLSearchParams(window.location.search);
    const debugParam = [...urlParams.keys()].find(key => key.toLowerCase() === 'debugh5p');

    // In local dev, enable when debugH5P parameter is present (any value)
    if (isLocalDev) {
      return !!debugParam;
    }

    // In production, require explicit configuration AND URL param
    return debugParam ? urlParams.get(debugParam) === '1' : false;
  };

  const extractH5pLibraryType = (iframe: HTMLIFrameElement): Promise<string | null> => {
    return new Promise((resolve) => {
      const checkForH5P = () => {
        try {
          const h5pIntegration = iframe.contentWindow?.H5PIntegration;
          if (h5pIntegration) {
            
            // Extract library type from contents
            const contents = h5pIntegration.contents;
            if (contents) {
              const firstContentKey = Object.keys(contents)[0];
              if (firstContentKey && contents[firstContentKey]?.library) {
                const libraryType = contents[firstContentKey].library;
                
                resolve(libraryType);
                return;
              }
            }
          } else {
            console.log('⚠️ H5PIntegration not found in iframe');
          }
          resolve(null);
        } catch (error) {
          console.log('❌ Error accessing H5PIntegration:', error);
          resolve(null);
        }
      };
      
      // Wait for H5P to initialize
      setTimeout(checkForH5P, 1000);
    });
  };

  return {
    provide: {
      h5pDebug: {
        isEnabled: isDebugEnabled,
        extractLibraryType: extractH5pLibraryType,
        getCurrentUrl: () => process.client ? window.location.href : ''
      }
    }
  };
});